// src/views/Vouchers/ViewRedemptionsModal.js
import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
  Box,
  Button,
  List,
  ListItem,
  ListItemText,
} from "@mui/material";
import Close from "@mui/icons-material/Close";
import ExpandMore from "@mui/icons-material/ExpandMore";
import { getAuthCredentials } from "../../../utils/auth";

const ViewRedemptionsModal = ({ open, onClose, voucherId }) => {
  const { token } = getAuthCredentials();
  const [loading, setLoading] = useState(false);
  const [voucherName, setVoucherName] = useState("");
  const [redemptions, setRedemptions] = useState([]);
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (!open || !voucherId) return;

    const fetchRedemptions = async () => {
      setLoading(true);
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/v1/api/vouchers/view-redemptions/${voucherId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setVoucherName(res.data.voucherName || "");
        setRedemptions(res.data.redemptions || []);
      } catch (err) {
        console.error("Error fetching redemptions:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchRedemptions();
  }, [open, voucherId, token]);

  const filtered = useMemo(() => {
    if (!search) return redemptions;
    const q = search.toLowerCase();
    return redemptions
      .map((rec) => ({
        ...rec,
        users: rec.users.filter((u) => u.name.toLowerCase().includes(q)),
      }))
      .filter(
        (rec) => rec.users.length > 0 || rec.date.toLowerCase().includes(q)
      );
  }, [search, redemptions]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      scroll="paper"
    >
      <DialogTitle sx={{ display: "flex", alignItems: "center", pr: 6 }}>
        Voucher Redemptions — {voucherName}
        <IconButton
          onClick={onClose}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <TextField
          label="Search name or date"
          variant="outlined"
          fullWidth
          size="small"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ mb: 2 }}
        />

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        ) : filtered.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No redemptions found.
          </Typography>
        ) : (
          filtered.map(({ date, users }) => (
            <Accordion key={date} defaultExpanded sx={{ mb: 1 }}>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography fontWeight={600}>
                  {new Date(date).toLocaleDateString()}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <List dense>
                  {users.map((u) => (
                    <ListItem key={u._id}>
                      <ListItemText primary={`${u.name} — used ${u.used}x`} />
                    </ListItem>
                  ))}
                </List>
              </AccordionDetails>
            </Accordion>
          ))
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ViewRedemptionsModal;
